// Send (read-only stepper). v1 does not send from the UI: step 4 shows the exact CLI command instead.
import { CaretRight, Check, ClockCounterClockwise, Info, Lock, Terminal } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";
import { Av, Ic, Journey, useTick } from "@/components/bits";
import { CliCommand, SampleBubble, SenderStrip } from "@/components/shared";
import { EVENT } from "@/lib/fields";
import { ago, dayClock, dur, fmt, whenLabel } from "@/lib/format";
import { capLevel } from "@/model/cap";
import { sendCommand } from "@/model/runs";
import { useApp } from "@/shell/appData";

function Step({
  k,
  cls,
  title,
  meta,
  children,
}: {
  k: number;
  cls: "done" | "wait" | "cur" | "locked" | "";
  title: string;
  meta?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <li className={`sp-i ${cls}`} style={{ "--k": k } as CSSProperties} data-step={k}>
      <span className="sp-n">
        {cls === "done" ? <Ic i={Check} w="bold" /> : cls === "locked" ? <Ic i={Lock} w="bold" /> : k}
      </span>
      <div className="sp-c">
        <div className="sp-h">
          <h3>{title}</h3>
          <span className="sp-m">{meta}</span>
        </div>
        {children ? <div className="sp-b">{children}</div> : null}
      </div>
    </li>
  );
}

export function Send() {
  const { next, cap, counts, config, runRows } = useApp();
  const now = useTick(30_000);
  const id = runRows.length + 1;
  const pct = cap.cap ? Math.min(100, (cap.used / cap.cap) * 100) : 0;
  const last = runRows[0];
  const cmd = sendCommand(EVENT.slug, next.messages);
  const timing = next.waitsForCap
    ? `Today’s cap is full. Batch ${id} would wait: a slot frees ${cap.freesAt ? `${whenLabel(cap.freesAt, now)} (in ${dur(cap.freesAt - now)})` : "later"}.`
    : next.phones < next.waiting
      ? `${fmt(next.phones)} phones fit in what is left of today’s cap. The other ${fmt(next.waiting - next.phones)} wait for a later batch.`
      : `All ${fmt(next.phones)} phones fit in today’s cap. They can go now.`;

  return (
    <div className="page compact">
      <div className="pghead">
        <div className="t">
          <h1>Send</h1>
          <p>
            Confirmations go out in batches of at most {fmt(cap.cap)} phones, the daily cap for this number. Sending
            runs from the CLI.
          </p>
        </div>
      </div>
      <SenderStrip />
      <ol className="stepper">
        <Step
          k={1}
          cls={config?.template ? "done" : "wait"}
          title="Template"
          meta={
            config?.template ? (
              <span className="badge soft">Checked with Meta by the CLI</span>
            ) : (
              <span className="badge warn">Run wa publish</span>
            )
          }
        >
          <div className="card sp-card">
            <div className="tpl-h">
              <span className="mono">{config?.template ?? "—"}</span>
              <span className="muted sm">English · from wa_meta/config</span>
            </div>
            <details className="pv">
              <summary>
                <Ic i={CaretRight} w="bold" />
                Preview the message
              </summary>
              <SampleBubble p={next.list[0]} label="Sample · first person in line" />
            </details>
          </div>
        </Step>
        <Step
          k={2}
          cls={next.phones ? "done" : "locked"}
          title={`Who’s in batch ${id}`}
          meta={
            <Link className="link sm" to="/people" search={{ s: "queued" }}>
              All {fmt(next.waiting)} waiting
            </Link>
          }
        >
          <div className="card sp-card" data-testid="next-batch">
            <div className="kv">
              <div>
                <div className="k">Messages</div>
                <div className="v" data-testid="nb-messages">
                  {fmt(next.messages)}
                </div>
                <div className="s">one per registration</div>
              </div>
              <div>
                <div className="k">Phones</div>
                <div className="v" data-testid="nb-phones">
                  {fmt(next.phones)}
                </div>
                <div className="s">count toward the cap</div>
              </div>
              <Link to="/people" search={{ s: "queued" }}>
                <div className="k">Still waiting after</div>
                <div className="v">{fmt(Math.max(0, next.waiting - next.phones))}</div>
                <div className="s link">see who</div>
              </Link>
            </div>
            <div className="names">
              {next.list.slice(0, 6).map((p) => (
                <span className="n" key={p.id}>
                  <Av name={p.name} />
                  <span className="nt">{p.name}</span>
                  {p.regs.length > 1 ? <span className="muted"> +{p.regs.length - 1}</span> : null}
                  <Journey s={p.status} />
                </span>
              ))}
              {next.phones > 6 ? (
                <Link
                  className="link sm"
                  to="/people"
                  search={{ s: "queued" }}
                  style={{ alignSelf: "center", padding: "0 6px" }}
                >
                  and {fmt(next.phones - 6)} more
                </Link>
              ) : null}
            </div>
            <p className="sp-p">
              Skipped on their own:{" "}
              <Link className="link" to="/people" search={{ s: "invalid" }}>
                {counts.byStatus.invalid} invalid numbers
              </Link>
              ,{" "}
              <Link className="link" to="/people" search={{ s: "opted_out" }}>
                {counts.byStatus.opted_out} opted out
              </Link>
              , and anyone who already has a confirmation. Failed sends that never reached WhatsApp are retried.
            </p>
          </div>
        </Step>
        <Step
          k={3}
          cls={next.waitsForCap ? "wait" : "done"}
          title="Cap & timing"
          meta={
            <span className="num muted sm">
              {fmt(cap.used)} / {fmt(cap.cap)} used today
            </span>
          }
        >
          <div className="card sp-card">
            <div className={`meter lg ${capLevel(cap.used, cap.cap)}`} data-level={capLevel(cap.used, cap.cap)}>
              <i style={{ width: `${pct}%` }} />
            </div>
            <p className="sp-p">{timing} The cap counts different phones in a rolling 24 hours.</p>
            {last ? (
              <p className="sp-p">
                Last run: <b>{last.label}</b> · {last.run.startedAt ? dayClock(last.run.startedAt) : "—"} (
                {ago(last.run.startedAt, now)}) · {fmt(last.messages)} messages · {last.stateText}
              </p>
            ) : (
              <p className="sp-p">No runs yet.</p>
            )}
          </div>
        </Step>
        <Step k={4} cls={next.messages ? "cur" : "locked"} title="Run it from the CLI">
          {next.messages ? (
            <div className="card sp-card">
              <div className="sum">
                <div>
                  <span>People (phones)</span>
                  <b>{fmt(next.phones)}</b>
                </div>
                <div>
                  <span>Messages · one per registration</span>
                  <b>{fmt(next.messages)}</b>
                </div>
                <div>
                  <span>Families in this batch</span>
                  <b>{next.families}</b>
                </div>
                <div>
                  <span>Template</span>
                  <b>
                    <span className="mono" style={{ fontSize: 11.5 }}>
                      {config?.template ?? "—"}
                    </span>
                  </b>
                </div>
                <div>
                  <span>Cap left today</span>
                  <b style={next.waitsForCap ? { color: "var(--warning)" } : undefined}>
                    {fmt(cap.left)} of {fmt(cap.cap)}
                  </b>
                </div>
                <div>
                  <span>Starts</span>
                  <b>{next.waitsForCap && cap.freesAt ? whenLabel(cap.freesAt, now) : "When you run it"}</b>
                </div>
              </div>
              <div className="label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Ic i={Terminal} />
                From the repo root
              </div>
              <CliCommand cmd={cmd} />
              <div className="note">
                <Ic i={Info} />
                <div>
                  <b>Sending stays in the CLI for now.</b> It checks the template with Meta, shows a summary and counts
                  down 10 seconds before it sends. Once a message is with WhatsApp it can’t be recalled.
                </div>
              </div>
            </div>
          ) : (
            <div className="note">
              <Ic i={Info} />
              <div>Nobody is waiting. New sign-ups show up after the next `wa publish`.</div>
            </div>
          )}
        </Step>
      </ol>
      <div className="section">
        <Link className="row card" to="/history" style={{ borderRadius: "var(--radius)" }}>
          <span className="ic pri">
            <Ic i={ClockCounterClockwise} />
          </span>
          <span className="tx">
            <b>Send history</b>
            <span>{runRows.length} batches so far · who sent what, and why each one stopped</span>
          </span>
          <Ic i={CaretRight} className="chev" />
        </Link>
      </div>
    </div>
  );
}
